const textEncoder = new TextEncoder()

async function blobArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  const modernBlob = blob as Blob & { arrayBuffer?: () => Promise<ArrayBuffer> }
  if (typeof modernBlob.arrayBuffer === 'function') return modernBlob.arrayBuffer()
  return await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => reader.result instanceof ArrayBuffer ? resolve(reader.result) : reject(new Error('Blob could not be read'))
    reader.onerror = () => reject(reader.error ?? new Error('Blob could not be read'))
    reader.readAsArrayBuffer(blob)
  })
}

function uint16(value: number) {
  return [value & 0xff, (value >>> 8) & 0xff]
}

function uint32(value: number) {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff]
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (const byte of bytes) {
    crc ^= byte
    for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
  }
  return (crc ^ 0xffffffff) >>> 0
}

function concat(chunks: Uint8Array[]): Uint8Array {
  const result = new Uint8Array(chunks.reduce((size, chunk) => size + chunk.length, 0))
  let offset = 0
  chunks.forEach((chunk) => { result.set(chunk, offset); offset += chunk.length })
  return result
}

export async function createStoreOnlyZip(files: Array<{ name: string; blob: Blob }>): Promise<Blob> {
  const localChunks: Uint8Array[] = []
  const centralChunks: Uint8Array[] = []
  let localOffset = 0

  for (const file of files) {
    const name = textEncoder.encode(file.name)
    const bytes = new Uint8Array(await blobArrayBuffer(file.blob))
    const checksum = crc32(bytes)
    const localHeader = new Uint8Array([
      ...uint32(0x04034b50), ...uint16(20), ...uint16(0x0800), ...uint16(0),
      ...uint16(0), ...uint16(0), ...uint32(checksum), ...uint32(bytes.length), ...uint32(bytes.length),
      ...uint16(name.length), ...uint16(0), ...name,
    ])
    localChunks.push(localHeader, bytes)

    centralChunks.push(new Uint8Array([
      ...uint32(0x02014b50), ...uint16(20), ...uint16(20), ...uint16(0x0800), ...uint16(0),
      ...uint16(0), ...uint16(0), ...uint32(checksum), ...uint32(bytes.length), ...uint32(bytes.length),
      ...uint16(name.length), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0), ...uint32(0),
      ...uint32(localOffset), ...name,
    ]))
    localOffset += localHeader.length + bytes.length
  }

  const central = concat(centralChunks)
  const end = new Uint8Array([
    ...uint32(0x06054b50), ...uint16(0), ...uint16(0), ...uint16(files.length), ...uint16(files.length),
    ...uint32(central.length), ...uint32(localOffset), ...uint16(0),
  ])
  return new Blob([concat([...localChunks, central, end])], { type: 'application/zip' })
}

export async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blobArrayBuffer(blob))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function serializeReportSvg(svg: SVGSVGElement): string {
  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('width', '1920')
  clone.setAttribute('height', '1080')
  return new XMLSerializer().serializeToString(clone)
}

export async function renderReportPng(svg: SVGSVGElement): Promise<Blob> {
  const source = serializeReportSvg(svg)
  const sourceBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(sourceBlob)
  try {
    const image = new Image()
    image.decoding = 'sync'
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('The report page could not be rendered as an image'))
      image.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = 1920
    canvas.height = 1080
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Image export is not available in this browser')
    context.drawImage(image, 0, 0, 1920, 1080)
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('PNG generation failed')), 'image/png'))
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
